import { For, Show, createSignal } from 'solid-js';
import { Button } from './Button';

/** Drag either clock hand to choose a time; keyboard sliders remain accessible. */
export const TimePicker = (props: {
  hour: string;
  minute: string;
  disabled?: boolean;
  onChange: (hour: string, minute: string) => void;
}) => {
  type Hand = 'hour' | 'minute';
  const [is24Hour, setIs24Hour] = createSignal(true);
  const hour = () => Number(props.hour) || 0;
  const minute = () => Number(props.minute) || 0;
  const pm = () => hour() >= 12;
  const value = (hand: Hand) =>
    hand === 'hour' ? (is24Hour() ? hour() : hour() % 12 || 12) : minute();
  const steps = (hand: Hand) => (hand === 'hour' ? (is24Hour() ? 24 : 12) : 60);

  let activePointer: number | null = null;

  const select = (hand: Hand, value: number) => {
    if (hand === 'hour')
      props.onChange(String(is24Hour() ? value : (value % 12) + (pm() ? 12 : 0)), props.minute);
    else props.onChange(props.hour, String(value));
  };
  const disabled = (element: Element) =>
    props.disabled || Boolean(element.closest('fieldset:disabled'));
  const selectAt = (event: PointerEvent & { currentTarget: SVGGElement }, hand: Hand) => {
    const rect = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
    if (!rect) return;
    const x = event.clientX - rect.left - rect.width / 2;
    const y = event.clientY - rect.top - rect.height / 2;
    if (Math.hypot(x, y) < 8) return;
    const turns = (Math.atan2(x, -y) / (2 * Math.PI) + 1) % 1;
    select(hand, Math.round(turns * steps(hand)) % steps(hand));
  };

  return (
    <fieldset disabled={props.disabled} class="min-w-0 space-y-3">
      <legend class="mb-2 text-sm font-medium">Time</legend>
      <div class="rounded-xl border border-neutral-200 bg-white p-3 dark:border-neutral-700 dark:bg-neutral-950">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div class="flex gap-3 text-sm font-semibold" aria-label="Selected time">
            <span class="text-brand-700 dark:text-brand-300">
              H · {String(hour()).padStart(2, '0')}
            </span>
            <span class="text-amber-700 dark:text-amber-300">
              M · {String(minute()).padStart(2, '0')}
            </span>
          </div>
          <div class="flex gap-1" role="group" aria-label="Time format">
            <For each={[true, false]}>
              {(format) => (
                <Button
                  size="sm"
                  variant={is24Hour() === format ? 'secondary' : 'ghost'}
                  aria-pressed={is24Hour() === format}
                  onClick={() => setIs24Hour(format)}
                >
                  {format ? '24h' : '12h'}
                </Button>
              )}
            </For>
          </div>
          <Show when={!is24Hour()}>
            <div class="flex gap-1" role="group" aria-label="Time period">
              <For each={['AM', 'PM']}>
                {(period) => (
                  <Button
                    size="sm"
                    variant={pm() === (period === 'PM') ? 'secondary' : 'ghost'}
                    aria-pressed={pm() === (period === 'PM')}
                    onClick={() =>
                      props.onChange(
                        String((hour() % 12) + (period === 'PM' ? 12 : 0)),
                        props.minute,
                      )
                    }
                  >
                    {period}
                  </Button>
                )}
              </For>
            </div>
          </Show>
        </div>
        <div
          class="mx-auto my-3 aspect-square w-full max-w-72 touch-none select-none rounded-full bg-neutral-100 dark:bg-neutral-800"
          classList={{ 'opacity-40': props.disabled }}
        >
          <svg viewBox="0 0 240 240" aria-label="Clock" class="h-full w-full">
            <title>Drag the short hour hand or long minute hand</title>
            {/* biome-ignore lint/a11y/noAriaHiddenOnFocusable: Decorative SVG group has no tab stop or focus handlers. */}
            <g aria-hidden="true" class="pointer-events-none">
              <For each={Array.from({ length: 60 }, (_, i) => i)}>
                {(tick) => {
                  const angle = (tick * Math.PI) / 30;
                  const radius = tick % 5 === 0 ? 109 : 113;
                  return (
                    <line
                      x1={120 + Math.sin(angle) * radius}
                      y1={120 - Math.cos(angle) * radius}
                      x2={120 + Math.sin(angle) * 117}
                      y2={120 - Math.cos(angle) * 117}
                      stroke="currentColor"
                      class="text-neutral-400 dark:text-neutral-500"
                    />
                  );
                }}
              </For>
              <For each={Array.from({ length: 12 }, (_, i) => i)}>
                {(i) => (
                  <text
                    x={120 + Math.sin((i * Math.PI) / 6) * 101}
                    y={120 - Math.cos((i * Math.PI) / 6) * 101}
                    text-anchor="middle"
                    dominant-baseline="central"
                    class="fill-amber-700 text-[9px] dark:fill-amber-300"
                  >
                    {String(i * 5).padStart(2, '0')}
                  </text>
                )}
              </For>
              <For each={Array.from({ length: is24Hour() ? 24 : 12 }, (_, i) => i)}>
                {(i) => (
                  <text
                    x={120 + Math.sin((i * Math.PI * 2) / steps('hour')) * 72}
                    y={120 - Math.cos((i * Math.PI * 2) / steps('hour')) * 72}
                    text-anchor="middle"
                    dominant-baseline="central"
                    class="fill-brand-700 text-[9px] dark:fill-brand-300"
                  >
                    {is24Hour() ? String(i).padStart(2, '0') : i || 12}
                  </text>
                )}
              </For>
            </g>
            <For each={['minute', 'hour'] as const}>
              {(hand) => {
                const angle = () => (value(hand) / steps(hand)) * Math.PI * 2;
                const radius = hand === 'hour' ? 72 : 101;
                const x = () => 120 + Math.sin(angle()) * radius;
                const y = () => 120 - Math.cos(angle()) * radius;
                return (
                  <g
                    role="slider"
                    aria-label={hand === 'hour' ? 'Clock hour' : 'Clock minute'}
                    aria-valuemin={hand === 'hour' && !is24Hour() ? 1 : 0}
                    aria-valuemax={hand === 'hour' ? (is24Hour() ? 23 : 12) : 59}
                    aria-valuenow={value(hand)}
                    aria-valuetext={
                      hand === 'hour'
                        ? is24Hour()
                          ? `${value(hand)} hours`
                          : `${value(hand)} ${pm() ? 'PM' : 'AM'}`
                        : `${value(hand)} minutes`
                    }
                    aria-disabled={props.disabled}
                    tabindex={props.disabled ? -1 : 0}
                    class="group cursor-grab outline-none"
                    classList={{
                      'text-brand-600': hand === 'hour',
                      'text-amber-600': hand === 'minute',
                    }}
                    onPointerDown={(event) => {
                      if (
                        disabled(event.currentTarget) ||
                        event.button !== 0 ||
                        activePointer !== null
                      )
                        return;
                      activePointer = event.pointerId;
                      event.currentTarget.setPointerCapture(event.pointerId);
                      event.currentTarget.focus({ preventScroll: true });
                      selectAt(event, hand);
                    }}
                    onPointerMove={(event) => {
                      if (activePointer === event.pointerId && !disabled(event.currentTarget))
                        selectAt(event, hand);
                    }}
                    onPointerUp={(event) => {
                      if (activePointer !== event.pointerId) return;
                      if (!disabled(event.currentTarget)) selectAt(event, hand);
                      activePointer = null;
                      event.currentTarget.releasePointerCapture(event.pointerId);
                    }}
                    onPointerCancel={() => {
                      activePointer = null;
                    }}
                    onLostPointerCapture={() => {
                      activePointer = null;
                    }}
                    onKeyDown={(event) => {
                      if (disabled(event.currentTarget)) return;
                      const delta = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[
                        event.key
                      ];
                      if (delta !== undefined)
                        select(hand, (value(hand) + delta + steps(hand)) % steps(hand));
                      else if (event.key === 'Home')
                        select(hand, hand === 'hour' && !is24Hour() ? 1 : 0);
                      else if (event.key === 'End')
                        select(hand, hand === 'hour' ? (is24Hour() ? 23 : 12) : 59);
                      else return;
                      event.preventDefault();
                    }}
                  >
                    <line
                      x1="120"
                      y1="120"
                      x2={120 + Math.sin(angle()) * (radius - 14)}
                      y2={120 - Math.cos(angle()) * (radius - 14)}
                      stroke="currentColor"
                      stroke-width={hand === 'hour' ? 5 : 3}
                      stroke-linecap="round"
                    />
                    <line
                      x1={120 + Math.sin(angle()) * 24}
                      y1={120 - Math.cos(angle()) * 24}
                      x2={x()}
                      y2={y()}
                      stroke="transparent"
                      stroke-width="16"
                    />
                    <circle
                      cx={x()}
                      cy={y()}
                      r="14"
                      stroke="currentColor"
                      stroke-width="2"
                      class="fill-neutral-100 drop-shadow-sm dark:fill-neutral-800 group-focus-visible:stroke-neutral-900 dark:group-focus-visible:stroke-white group-focus-visible:stroke-4"
                    />
                    <text
                      x={x()}
                      y={y()}
                      text-anchor="middle"
                      dominant-baseline="central"
                      fill="currentColor"
                      class="pointer-events-none text-base font-bold"
                    >
                      {String(value(hand)).padStart(2, '0')}
                    </text>
                  </g>
                );
              }}
            </For>
            <circle
              cx="120"
              cy="120"
              r="4"
              class="pointer-events-none fill-neutral-600 dark:fill-neutral-300"
            />
          </svg>
        </div>
        <p class="text-center text-xs text-neutral-500">
          Drag the short hand for hours or the long hand for minutes. Use arrow keys on either hand.
        </p>
      </div>
    </fieldset>
  );
};
